import React, { useEffect, useRef, useState } from 'react';
import { Brand, BROWSE_LABEL, CATALOG_CATEGORIES, getSceneProducts, Market, Product, SceneGroup } from '../lib/productsApi';
import { formatPrice, Currency } from '../lib/currency';
import { X, Plus, ExternalLink, Search } from './Icons';

interface ProductPanelProps {
  /**
   * Shoppable labels detected in the room, most prominent first. May be empty —
   * the panel opens from any photo, and the server answers a label-less request
   * with a catalog browse group rather than nothing.
   */
  labels: string[];
  onAddReference: (product: Product) => Promise<void>;
  onClose: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/** What the server scoped to, said plainly — there is nothing to pick here. */
const MARKET_LABEL: Record<Market, string> = {
  IN: 'Prices in ₹ · shipping to India',
  US: 'Prices in $ · shipping to the US',
};

const BRAND_LABEL: Record<Brand, string> = { wakefit: 'Wakefit', ikea: 'IKEA', wayfair: 'Wayfair' };

/**
 * Which retailers actually stock the resolved market — Wayfair is US-only and
 * Wakefit is IN-only, so a chip that can only ever return zero results never
 * renders. IKEA is the one brand present in both.
 */
const BRANDS_BY_MARKET: Record<Market, Brand[]> = {
  IN: ['wakefit', 'ikea'],
  US: ['ikea', 'wayfair'],
};

function sourceLabel(source?: string | null): string {
  if (!source) return 'View product';
  return `View on ${source.charAt(0).toUpperCase()}${source.slice(1)}`;
}

/** IKEA names are codewords ("LENDA"); show the type unless it's already there. */
function subtitleFor(p: Product): string | null {
  const bits = [p.brand, p.productType].filter(Boolean) as string[];
  const name = p.name.toLowerCase();
  const useful = bits.filter(b => !name.includes(b.toLowerCase()));
  return useful.length ? useful.join(' · ') : null;
}

export function ProductPanel({ labels, onAddReference, onClose }: ProductPanelProps) {
  const [groups, setGroups]   = useState<SceneGroup[] | null>(null);
  const [active, setActive]   = useState<string | null>(null);
  const [query, setQuery]     = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // Empty = no brand filter. Applies across every group, same as `query`.
  const [brands, setBrands] = useState<Brand[]>([]);
  const toggleBrand = (b: Brand) => {
    setBrands(prev => (prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]));
  };

  // '' = no manual category pick — the panel just shows what got detected.
  // Lets a user shop a category the photo never surfaced at all; when it's set
  // the server adds one extra group for it, which we jump straight to (see the
  // fetch effect below), regardless of whether that category was in the photo.
  const [category, setCategory] = useState('');
  const prevCategory = useRef('');

  // The market is the server's to decide (see lib/geo.ts) — the client never
  // asks for one. We only keep what came back, to caption the prices with it.
  const [market, setMarket] = useState<Market | null>(null);

  const [addingId, setAddingId]   = useState<string | null>(null);
  const [addedIds, setAddedIds]   = useState<Set<string>>(new Set());
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});

  // Guards against a slow earlier request overwriting a newer one's results.
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const trimmed = query.trim();
    setLoading(true);

    // Only jump the active tab to the picked category on the request that
    // introduced/changed it — not on every keystroke/brand toggle afterwards,
    // which would yank the user back after they'd clicked onto another tab.
    const categoryJustChanged = prevCategory.current !== category;
    prevCategory.current = category;

    const timer = setTimeout(() => {
      getSceneProducts(labels, trimmed || undefined, undefined, brands.length ? brands : undefined, category || undefined)
        .then(({ country, groups: next }) => {
          if (seq !== requestSeq.current) return;
          setGroups(next);
          setMarket(country);
          setError('');
          setActive(prev => {
            if (categoryJustChanged && category && next.some(g => g.label === category)) return category;
            // Keep the open tab when it survives the new query; else fall back.
            return prev && next.some(g => g.label === prev) ? prev : next[0]?.label ?? null;
          });
        })
        .catch(() => {
          if (seq !== requestSeq.current) return;
          setError('Could not load products. Please try again.');
        })
        .finally(() => {
          if (seq === requestSeq.current) setLoading(false);
        });
    }, trimmed ? SEARCH_DEBOUNCE_MS : 0);

    return () => clearTimeout(timer);
  }, [labels, query, brands, category]);

  const handleAdd = async (p: Product) => {
    setAddingId(p.id);
    setAddErrors(prev => { const next = { ...prev }; delete next[p.id]; return next; });
    try {
      await onAddReference(p);
      setAddedIds(prev => new Set(prev).add(p.id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not add reference image.';
      setAddErrors(prev => ({ ...prev, [p.id]: message }));
    } finally {
      setAddingId(null);
    }
  };

  const activeGroup = groups?.find(g => g.label === active) ?? null;
  // Only reachable when the catalog itself had nothing for this market — the
  // server falls back to a browse group whenever the scene comes up short.
  const catalogEmpty = !loading && !error && groups !== null && groups.length === 0;
  const browsing = !!activeGroup && activeGroup.label === BROWSE_LABEL;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="product-panel" role="dialog" aria-label="Product suggestions" onClick={e => e.stopPropagation()}>
        <div className="product-panel-header">
          <h3 className="heading-sm">Shop the look</h3>
          <div className="product-panel-header-actions">
            {market && <span className="product-market-note">{MARKET_LABEL[market]}</span>}
            <button type="button" className="product-panel-close" onClick={onClose} aria-label="Close">

              <X size={18} />
            </button>
          </div>
        </div>

        {!catalogEmpty && (
          <div className="product-search-row">
            <Search size={14} />
            <input
              type="search"
              className="product-search-input"
              placeholder={browsing ? 'Search all products…' : 'Search these products…'}
              aria-label="Search products"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        )}

        {/* Always present, whatever the photo did or didn't detect — lets the
            user shop a category outside the scanned tabs below. */}
        {!catalogEmpty && (
          <div className="product-category-row">
            <label htmlFor="product-category-select" className="product-category-label">
              Browse a category
            </label>
            <select
              id="product-category-select"
              className="product-category-select"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              <option value="">Choose a category…</option>
              {CATALOG_CATEGORIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        {!!groups?.length && (
          <div className="product-tabs" role="tablist" aria-label="Detected objects">
            {groups.map(g => (
              <button
                key={g.label}
                type="button"
                role="tab"
                aria-selected={g.label === active}
                className={`product-tab${g.label === active ? ' product-tab--active' : ''}`}
                onClick={() => setActive(g.label)}
              >
                {g.label}
                <span className="product-tab-count">{g.total}</span>
              </button>
            ))}
          </div>
        )}

        {!catalogEmpty && market && (
          <div className="product-brand-row" role="group" aria-label="Filter by brand">
            {BRANDS_BY_MARKET[market].map(b => (
              <button
                key={b}
                type="button"
                aria-pressed={brands.includes(b)}
                className={`product-brand-chip${brands.includes(b) ? ' product-brand-chip--active' : ''}`}
                onClick={() => toggleBrand(b)}
              >
                {BRAND_LABEL[b]}
              </button>
            ))}
          </div>
        )}

        {/* Say why the panel isn't scene-matched, so browsing doesn't look like a bug. */}
        {browsing && groups?.length === 1 && !loading && !error && (
          <p className="product-panel-note">
            Nothing shoppable spotted in this room — showing the full catalog.
          </p>
        )}

        {error && <p className="form-error" style={{ padding: '0 16px' }}>{error}</p>}

        {loading && <div className="spinner" style={{ margin: '32px auto' }} />}

        {catalogEmpty && (
          <div className="product-panel-empty">
            <p>No products available right now.</p>
            <span className="caption">Check back soon — we’re adding more all the time.</span>
          </div>
        )}

        {!loading && !error && activeGroup?.products.length === 0 && (
          <div className="product-panel-empty">
            <p>{browsing ? 'No products match your search.' : `No matches for “${activeGroup.label}”.`}</p>
            <span className="caption">
              {query.trim() ? 'Try a different search term.' : 'Check back soon — we’re adding more all the time.'}
            </span>
          </div>
        )}

        {!loading && !error && !!activeGroup?.products.length && (
          <div className="product-list">
            {activeGroup.products.map(p => {
              const subtitle = subtitleFor(p);
              return (
                <div className="product-card" key={p.id}>
                  {p.imageUrls[0] && <img className="product-card-img" src={p.imageUrls[0]} alt={p.name} loading="lazy" />}
                  <div className="product-card-info">
                    <p className="product-card-name">{p.name}</p>
                    {subtitle && <p className="product-card-subtitle">{subtitle}</p>}
                    <p className="product-card-price">
                      {formatPrice(String(p.price), p.currency as Currency)}
                      {p.mrp !== null && p.mrp > p.price && (
                        <span className="product-card-mrp">{formatPrice(String(p.mrp), p.currency as Currency)}</span>
                      )}
                    </p>
                    <div className="product-card-actions">
                      <button
                        type="button"
                        className="product-add-btn"
                        onClick={() => handleAdd(p)}
                        disabled={addingId === p.id || addedIds.has(p.id)}
                      >
                        {addedIds.has(p.id)
                          ? 'Added ✓'
                          : addingId === p.id
                            ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                            : <><Plus size={13} /> Reference</>}
                      </button>
                      <a className="product-link-btn" href={p.url} target="_blank" rel="noopener noreferrer" title={sourceLabel(p.source)}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                    {addErrors[p.id] && <p className="form-error product-card-error">{addErrors[p.id]}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
